import { Address } from "./address.interface";
import { Currency } from "./currency.interface";
import { Discount } from "./discount.interface";

export enum PaymentStatus {
  REQUESTED = "REQUESTED",
  SUCCESSFUL = "SUCCESSFUL",
  INCOMPLETE = "INCOMPLETE",
  ACTION_REQUIRED = "ACTION_REQUIRED",
  FAILED = "FAILED",
  CAPTURED = "CAPTURED",
  IN_PROGRESS = "IN_PROGRESS",
  DISPUTED = "DISPUTED",
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
  REFUNDED = "REFUNDED",
  CANCELLED = "CANCELLED",
}

export enum PaymentType {
  CARD_STRIPE = "CARD_STRIPE",
  TERMINAL_STRIPE = "TERMINAL_STRIPE",
  ACH_STRIPE = "ACH_STRIPE",
  CARD_FWD = "CARD_FWD",
  ACH_FWD = "ACH_FWD",
  CASH = "CASH",
  CHECK = "CHECK",
  LOAN = "LOAN",
  ACCOUNT = "ACCOUNT",
  PAYMENT = "PAYMENT",
  NON_REV = "NON_REV",
  FUEL_REIMBURSEMENT = "FUEL_REIMBURSEMENT",
  FLIGHT_CREDIT = "FLIGHT_CREDIT",
  OTHER = "OTHER",
  PAYMENT_REQUEST = "PAYMENT_REQUEST",
}

export interface AircraftRates {
  [key: string]: { [key: string]: number };
}

export type PaymentOption = {
  type: PaymentType;
  amount: number;
  currency: Currency;
  id: string;
};

export interface PaymentLineItems {
  amount: number;
  invoiceId: string;
  lineItemId: string;
  qboLineItemId: string;
}

export interface Payment {
  createdAt: Date;
  userId: string;
  clubId: string;
  locationId: string;
  paymentId: string;
  transactionNumber: number;
  date: Date;
  type: PaymentType;
  paymentCurrency: Currency;
  status: PaymentStatus;
  amount: number;
  totalAmount: number;
  fees: number;
  unappliedAmount: number;
  clubPaysFees: boolean;
  lineItems?: PaymentLineItems[];
  discounts?: Discount[];
  aircraftRates?: AircraftRates;
  accountId?: string;
  description?: string;
  paymentData?: any[];
  qboPaymentId?: string;
  refunds?: any[];
}

export const paymentStatusDisplay = {
  [PaymentStatus.CAPTURED]: {
    text: "Captured",
    icon: "more_horiz",
    circular: true,
    color: "success",
  },
  [PaymentStatus.REQUESTED]: {
    text: "Requested",
    icon: "send",
    circular: false,
    color: "dark",
  },
  [PaymentStatus.SUCCESSFUL]: {
    text: "Successful",
    icon: "done",
    circular: true,
    color: "success",
  },
  [PaymentStatus.INCOMPLETE]: {
    text: "Incomplete",
    icon: "running_with_errors",
    circular: false,
    color: "warning",
  },
  [PaymentStatus.ACTION_REQUIRED]: {
    text: "Action Required",
    icon: "priority_high",
    circular: false,
    color: "error",
  },
  [PaymentStatus.FAILED]: {
    text: "Failed",
    icon: "close",
    circular: true,
    color: "error",
  },
  [PaymentStatus.IN_PROGRESS]: {
    text: "In Progress",
    icon: "more_horiz",
    circular: false,
    color: "warning",
  },
  [PaymentStatus.DISPUTED]: {
    text: "Disputed",
    icon: "close",
    circular: false,
    color: "error",
  },
  [PaymentStatus.PARTIALLY_REFUNDED]: {
    text: "Partially Refunded",
    icon: "replay",
    circular: false,
    color: "dark",
  },
  [PaymentStatus.REFUNDED]: {
    text: "Refunded",
    icon: "replay",
    circular: false,
    color: "dark",
  },
  [PaymentStatus.CANCELLED]: {
    text: "Cancelled",
    icon: "not_interested",
    circular: false,
    color: "dark",
  },
};

export interface PaymentMethod {
  id: string;
  token?: string;
  additionalData: AdditionalData;
  type: PaymentType;
  displayName: string;
  calculatedRate?: number;
  derivedCategory?: string;
}

export interface AdditionalData {
  type: string;
  accountId?: string;
  createdBy?: string;
  createdAt?: string;
  userId?: string;
  clubId?: string;
  paymentId?: string;
  date?: string;
  paymentCurrency?: string;
  status?: string;
  totalAmount?: number;
  unappliedAmount?: number;
  balance?: number;
  discounts?: Discount[];
  aircraftRates?: { [key: string]: { [key: string]: number } };

  id?: string;
  object?: string;
  allow_redisplay?: string;
  billing_details?: BillingDetails;
  card?: Card;
  created?: number;
  customer?: string;
  livemode?: boolean;
  metadata?: Metadata;
  radar_options?: Metadata;

  expMonth?: string;
  expYear?: string;
  lastFourDigits?: string;
  brand?: string;
  country?: string;
  firstSixDigits?: string;
  postalCode?: string;
}

export interface BillingDetails {
  address: Address;
  email: null;
  name: string;
  phone: null;
}

export interface Card {
  brand: string;
  checks: Checks;
  country: string;
  display_brand: string;
  exp_month: number;
  exp_year: number;
  fingerprint: string;
  funding: string;
  generated_from: null;
  last4: string;
  networks: Networks;
  three_d_secure_usage: ThreeDSecureUsage;
  wallet: null;
}

export interface Checks {
  address_line1_check: string;
  address_postal_code_check: string;
  cvc_check: string;
}

export interface Networks {
  available: string[];
  preferred: null;
}

export interface ThreeDSecureUsage {
  supported: boolean;
}

export interface Metadata {}
